/**
 * TimeToFirstOKRChart Component
 * Displays time to first OKR metrics and distribution
 *
 * Story 5.2: Time to First OKR Metric
 */

import React, { useState } from 'react';
import { clsx } from 'clsx';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { Card } from '../../common';
import type { TimeToFirstOKRData } from '../../../services/reportApi';

export interface TimeToFirstOKRChartProps {
  /** Time to first OKR data */
  data: TimeToFirstOKRData;
  /** Loading state */
  isLoading?: boolean;
  /** Height of the chart */
  height?: number;
  /** Click handler for user */
  onUserClick?: (userId: string) => void;
  /** Additional class names */
  className?: string;
}

/**
 * Loading skeleton
 */
const ChartSkeleton: React.FC<{ height: number }> = ({ height }) => (
  <div className="animate-pulse">
    <div className="flex items-center justify-between mb-4">
      <div className="h-6 bg-stone-200 rounded w-40" />
      <div className="h-6 bg-stone-200 rounded w-24" />
    </div>
    <div style={{ height }} className="bg-stone-100 rounded" />
    <div className="grid grid-cols-3 gap-4 mt-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="text-center">
          <div className="h-8 bg-stone-200 rounded w-16 mx-auto mb-2" />
          <div className="h-4 bg-stone-200 rounded w-20 mx-auto" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Custom tooltip for the chart
 */
const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload?.[0]) return null;

  const data = payload[0].payload;
  return (
    <div className="bg-white border border-stone-200 rounded-lg shadow-lg p-3">
      <p className="font-medium text-stone-900">{data.range}</p>
      <p className="text-sm text-stone-600">
        {data.count} users ({data.percentage}%)
      </p>
    </div>
  );
};

/**
 * Time to First OKR Chart Component
 */
export const TimeToFirstOKRChart: React.FC<TimeToFirstOKRChartProps> = ({
  data,
  isLoading = false,
  height = 250,
  onUserClick,
  className,
}) => {
  const [showUsersWithoutOKR, setShowUsersWithoutOKR] = useState(false);

  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <ChartSkeleton height={height} />
      </Card>
    );
  }

  const {
    averageDays,
    medianDays,
    distribution,
    usersWithoutOKR,
    byRole,
  } = data;

  return (
    <Card className={clsx('p-6', className)}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-stone-900">Time to First OKR</h3>
          <p className="text-sm text-stone-500">Days from activation to first OKR creation</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-center">
            <div className="text-xl font-bold text-stone-900">{averageDays}d</div>
            <div className="text-xs text-stone-500">Average</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-stone-900">{medianDays}d</div>
            <div className="text-xs text-stone-500">Median</div>
          </div>
        </div>
      </div>

      {/* Distribution chart */}
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={distribution}
            margin={{ top: 10, right: 20, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
            <XAxis
              dataKey="range"
              tick={{ fontSize: 12, fill: '#78716c' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#78716c' }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="count"
              fill="#6366f1"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* By role breakdown */}
      {byRole.length > 0 && (
        <div className="mt-6 pt-4 border-t border-stone-200">
          <h4 className="text-sm font-medium text-stone-700 mb-3">By Role</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {byRole.map((role) => (
              <div key={role.role} className="p-3 bg-stone-50 rounded-lg text-center">
                <div className="text-sm font-medium text-stone-900 capitalize">
                  {role.role.replace('_', ' ')}
                </div>
                <div className="text-lg font-bold text-indigo-600">{role.averageDays}d</div>
                <div className="text-xs text-stone-500">{role.count} users</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Users without OKR */}
      {usersWithoutOKR.length > 0 && (
        <div className="mt-4 border border-amber-200 rounded-lg">
          <button
            onClick={() => setShowUsersWithoutOKR(!showUsersWithoutOKR)}
            className="w-full flex items-center justify-between p-3 bg-amber-50 rounded-t-lg hover:bg-amber-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">
                {usersWithoutOKR.length} users haven't created an OKR yet
              </span>
            </div>
            {showUsersWithoutOKR ? (
              <ChevronUp className="w-4 h-4 text-amber-600" />
            ) : (
              <ChevronDown className="w-4 h-4 text-amber-600" />
            )}
          </button>

          {showUsersWithoutOKR && (
            <div className="p-3 max-h-48 overflow-y-auto">
              <div className="space-y-2">
                {usersWithoutOKR.slice(0, 10).map((user) => (
                  <button
                    key={user.userId}
                    onClick={() => onUserClick?.(user.userId)}
                    className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-stone-100 transition-colors text-left"
                  >
                    <div>
                      <div className="text-sm font-medium text-stone-900">{user.name}</div>
                      <div className="text-xs text-stone-500">{user.email}</div>
                    </div>
                    <span className="text-xs text-amber-600 font-medium">
                      {user.daysSinceActivation}d since activation
                    </span>
                  </button>
                ))}
                {usersWithoutOKR.length > 10 && (
                  <p className="text-xs text-stone-500 text-center pt-2">
                    +{usersWithoutOKR.length - 10} more users
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default TimeToFirstOKRChart;
